<script setup>
/**
 * Where you are in the composer, and which of its screens you may jump back to.
 *
 * A NEAR-COPY OF `components/broadcast/ComposeStepRail.vue`, on purpose and almost line for
 * line. That file is the one place in this product that already gets a long, high-stakes,
 * multi-decision job right, and the redesign's whole argument is that the task builder
 * should have had it from the start. Copying it rather than generalising it keeps the
 * broadcast rail's 1,000-odd assertions exactly where they are, and the two lists genuinely
 * do differ: this one takes its labels from its own table, and its step ids are the task
 * composer's five.
 *
 * EVERY REACHABLE SCREEN IS A REAL LINK, not a click handler. `to` is `?step=<id>` on the
 * composer route, so a screen is a URL — the browser's own Back button walks the sequence
 * backwards, a screen can be bookmarked, and middle-clicking one opens it in a tab. A
 * `@click` mutating a `currentStep` ref would look identical and none of those would be true.
 *
 * A SCREEN YOU HAVE NOT EARNED IS NOT A LINK. Forward screens render as plain text, because
 * `helpers/task-compose.js` `resolveStep` would bounce a click on one straight back — and a
 * link that silently lands somewhere else is worse than one that was never offered. The
 * guard is the authority; this only agrees with it.
 *
 * The rail is a `<nav><ol>`: an ordered list of destinations, with `aria-current="step"` on
 * the one that is open, so a screen reader gets the position without the "Step 2 of 5" line
 * the sighted reader gets from the header.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** Screen ids in order, from `stepsFor(draft)`. */
  steps: { type: Array, required: true },
  current: { type: String, required: true },
  /** Ids the guard would allow — everything complete, plus the current one. */
  reachable: { type: Array, default: () => [] },
  /** `step => routeLocation`. */
  linkFor: { type: Function, required: true },
});

const { t } = useWasloI18n();

const KEY = {
  job: 'JOB',
  steps: 'STEPS',
  whatif: 'WHATIF',
  arabic: 'ARABIC',
  check: 'CHECK',
};

const rows = computed(() =>
  props.steps.map((step, index) => ({
    step,
    number: index + 1,
    label: t(`WASLO.TASKS.COMPOSE.RAIL.${KEY[step] ?? 'STEPS'}`),
    isCurrent: step === props.current,
    // Done means "behind the one you are on", which is the only definition that cannot
    // disagree with the guard: the guard admits a screen only when every screen before it
    // is complete, so anything before the current one is complete by construction.
    isDone: props.steps.indexOf(step) < props.steps.indexOf(props.current),
    isReachable: props.reachable.includes(step) && step !== props.current,
  }))
);
</script>

<template>
  <nav
    data-waslo-compose-rail
    :aria-label="t('WASLO.TASKS.COMPOSE.RAIL.LABEL')"
  >
    <ol class="flex flex-wrap items-center gap-x-2 gap-y-2 mb-0">
      <li
        v-for="(row, index) in rows"
        :key="row.step"
        class="flex items-center gap-2"
      >
        <component
          :is="row.isReachable ? 'router-link' : 'span'"
          :to="row.isReachable ? linkFor(row.step) : undefined"
          :aria-current="row.isCurrent ? 'step' : undefined"
          class="inline-flex items-center gap-2 h-8 px-2.5 rounded-lg text-sm"
          :class="[
            row.isCurrent
              ? 'bg-n-blue-3 text-n-blue-11 font-medium'
              : 'text-n-slate-11',
            row.isReachable ? 'hover:bg-n-alpha-2 cursor-pointer' : '',
            !row.isCurrent && !row.isDone && !row.isReachable
              ? 'text-n-slate-10'
              : '',
          ]"
          :data-waslo-compose-step="row.step"
          :data-waslo-compose-step-state="
            row.isCurrent ? 'current' : row.isDone ? 'done' : 'ahead'
          "
        >
          <span
            v-if="row.isDone"
            class="size-4 i-lucide-check text-n-blue-10"
            aria-hidden="true"
          />
          <span v-else class="w-4 text-xs tabular-nums" aria-hidden="true">
            {{ row.number }}
          </span>
          {{ row.label }}
        </component>
        <!--
          The separator mirrors on `rtl:`, because a chevron is the one glyph whose meaning
          IS its direction. Never after the last row.
        -->
        <span
          v-if="index < rows.length - 1"
          class="size-3 i-lucide-chevron-right rtl:rotate-180 text-n-slate-9"
          aria-hidden="true"
        />
      </li>
    </ol>
  </nav>
</template>
